/**
 * WebPreview — render a web source citation inside the SourceDrawer.
 *
 * Why a card, not an iframe? Major sites (Google, Notion, news outlets)
 * ship ``X-Frame-Options: DENY`` / ``frame-ancestors 'none'`` so the
 * naive iframe path silently produces a blank panel. The SOTA pattern
 * (Perplexity / Cursor / ChatGPT) is a richer "link preview" card with
 * the user owning the navigation decision via an explicit CTA — same
 * affordance as a search-result row.
 *
 * Vertical anatomy (Jun 2026, post UI/UX review):
 *   1. Host pill — favicon + hostname (origin anchor, glance-readable)
 *   2. Title — h2-style, links to source
 *   3. Snippet — chat-side excerpt that produced the citation
 *   4. URL (mono) — anti-phishing destination preview shown BEFORE CTA
 *      so the user sees the URL during decision time, not after click
 *   5. Published date — meta row below URL, near the CTA
 *   6. Primary CTA "Apri su {host}" — last commitment surface
 *
 * Accessibility:
 *   - Wrapper ``<article>`` carries the aria-label
 *   - Favicon uses declarative React state for the Lucide fallback —
 *     no imperative DOM mutation / refs (works with SSR + streaming)
 *   - ``referrerPolicy="no-referrer"`` on the favicon keeps the
 *     third-party Google service from tracking the chat URL
 *   - Three click targets (title link, URL line title attribute, CTA)
 *     are intentional for screen-magnifier users
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ArrowUpRight, Globe } from "lucide-react";
import * as React from "react";

import { cn } from "../../../lib/cn";
import { MessageBody } from "../../MessageBody";

export interface WebPayload {
  /** External URL — required (the resolver guarantees presence). */
  readonly url: string;
  /** Human-readable title — falls back to the hostname when absent. */
  readonly title?: string;
  /** Excerpted snippet rendered as the body copy. */
  readonly snippet?: string;
  /** ISO 8601 publish date (optional). */
  readonly publishedDate?: string;
}

export interface WebPreviewProps {
  readonly payload: WebPayload;
  readonly className?: string;
}

/** Pull the host out of an URL — falls back to the raw string when the
 *  URL constructor throws (relative/malformed). */
function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Google s2 favicon service URL — 32px crisp on retina at 16px box. */
function faviconUrl(hostname: string): string {
  return `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(hostname)}`;
}

/**
 * Defensive normaliser for Tavily-mangled markdown tables. The
 * web-fusion specialist sometimes ships ``s.content`` with the
 * inter-row ``\n`` collapsed into a single space — typically because
 * the upstream provider summarisation strips whitespace, or the BE
 * SQL TEXT column round-trips through a JSON serialiser that
 * normalises whitespace. The symptom users see:
 *
 *   "| Ano | Jan | Acc | | 2026 | 0,33 | 4.39 | | 2025 | …"
 *                       ^^^^^^                 ^^^^^^^
 *                       row boundary collapsed
 *
 * Two things must happen for ``remark-gfm`` to actually render a
 * table (CommonMark spec § 3.2: a single ``\n`` is collapsed to a
 * space, so just splitting rows is NOT enough on its own):
 *
 *   1. Split the row blob on the ``| | `` boundary onto distinct
 *      lines with ``\n`` between them.
 *   2. Inject the GFM separator row ``|---|---|---|`` between the
 *      header and the data rows — Tavily routinely strips it (it
 *      appears as ``-- -- -- |`` in the upstream HTML which collapses
 *      to ``| -- -- -- |`` on extraction, NOT a valid GFM separator).
 *
 * The heuristic is conservative — lines without ≥6 pipes are returned
 * verbatim, and the splitter only runs when at least 3 rows are
 * detected (otherwise it's likely a single row of in-prose pipes, not
 * a mangled table).
 */
function normaliseMangledMarkdownTables(text: string): string {
  const PIPE_DENSITY_THRESHOLD = 6;
  const ROW_BOUNDARY_RE = / \| \| /g;
  const MIN_ROW_COUNT = 3;
  // A "mangled separator" row from Tavily is a sequence of ``--``
  // groups (optionally separated by spaces) wedged between proper
  // pipe-delimited rows. Tracks the cells of the header row so the
  // synthetic separator matches its column count.
  const MANGLED_SEPARATOR_RE = /^\s*-+(?:\s+-+)*\s*$/;

  return text
    .split("\n")
    .map((line) => {
      const pipeCount = (line.match(/\|/g) ?? []).length;
      if (pipeCount < PIPE_DENSITY_THRESHOLD) return line;

      // Split on the row boundary; trim each row of stray leading /
      // trailing whitespace + leading pipes (we re-add them below).
      const parts = line.split(ROW_BOUNDARY_RE);
      if (parts.length < MIN_ROW_COUNT) {
        // Conservative bail-out: a single boundary match is more
        // likely a typo than a mangled table — leave the line alone.
        return line;
      }

      // Re-attach the leading / trailing ``|`` that the split removed
      // from the inner rows. The first row already starts with ``|``;
      // the last row already ends with ``|``. Middle rows need both.
      const rows = parts.map((part, i) => {
        let row = part.trim();
        if (i > 0 && !row.startsWith("|")) row = `| ${row}`;
        if (i < parts.length - 1 && !row.endsWith("|")) row = `${row} |`;
        return row;
      });

      // Compute the synthetic GFM separator from the header row's
      // pipe count. ``|---|---|---|`` for a 3-column header.
      const headerCells = (rows[0]?.match(/\|/g) ?? []).length - 1;
      const separator =
        headerCells > 0 ? `|${" --- |".repeat(headerCells)}` : "";

      // If the second row looks like a Tavily-mangled separator
      // (``| -- -- -- |``), replace it; otherwise inject a fresh one
      // after the header. ``remark-gfm`` needs an EXACT separator
      // row to flip the parser into table mode.
      const secondRowInner = rows[1]?.replace(/^\||\|$/g, "").trim() ?? "";
      if (rows.length > 1 && MANGLED_SEPARATOR_RE.test(secondRowInner)) {
        if (separator) rows[1] = separator;
      } else if (separator) {
        rows.splice(1, 0, separator);
      }

      return rows.join("\n");
    })
    .join("\n");
}

function formatPublishedDate(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return null;
  }
}

export const WebPreview: React.FC<WebPreviewProps> = ({ payload, className }) => {
  const { i18n } = useLingui();
  const host = hostnameOf(payload.url);
  const title = payload.title?.trim() || host;
  const formattedDate = payload.publishedDate
    ? formatPublishedDate(payload.publishedDate)
    : null;

  // Declarative favicon fallback (Jun 2026 review fix W1) — flipping a
  // ``display:none`` via imperative DOM ref breaks SSR/streaming and
  // de-syncs React's view of the tree. Local state is the canonical
  // pattern; we use the React 18+ "adjusting state during render"
  // idiom (NOT ``useEffect``) to reset the failed flag when the host
  // changes — the previous host's 404 must NOT poison the next host's
  // first paint, and an effect would introduce an extra render cycle
  // showing the wrong fallback briefly. Ref:
  //   https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [faviconFailed, setFaviconFailed] = React.useState(false);
  const [prevHost, setPrevHost] = React.useState(host);
  if (host !== prevHost) {
    setPrevHost(host);
    setFaviconFailed(false);
  }

  return (
    <article
      className={cn(
        "flex h-full flex-col gap-4 overflow-y-auto px-5 py-4",
        className,
      )}
      aria-label={i18n._(msg`Anteprima web ${title}`)}
    >
      {/* 1. Host pill — favicon + hostname. Mirrors the chip family used by
          MessageScopeChips so the visual rhythm stays cohesive. */}
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded bg-ink-1/[0.06]"
        >
          {faviconFailed ? (
            <Globe className="h-3.5 w-3.5 text-ink-3" />
          ) : (
            <img
              src={faviconUrl(host)}
              alt=""
              width={16}
              height={16}
              referrerPolicy="no-referrer"
              className="h-4 w-4 object-contain"
              onError={() => setFaviconFailed(true)}
            />
          )}
        </span>
        <span className="truncate font-medium text-[11px] text-ink-3 uppercase tracking-wide">
          {host}
        </span>
      </div>

      {/* 2. Title — links to the source. ``noopener noreferrer`` is a hard
          requirement on cross-origin _blank links (Spec § 4.6.4).
          aria-label trimmed (review S1): the title + the implicit
          ``target=_blank`` announcement from the screen reader covers
          the "new tab" context without a verbose label. */}
      <a
        href={payload.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "block text-balance font-semibold text-[18px] text-ink-1 leading-snug",
          "transition-colors hover:text-ando-orange",
          "focus-visible:underline focus-visible:decoration-2 focus-visible:decoration-ando-orange focus-visible:outline-none",
        )}
        aria-label={`${title} · ${host}`}
      >
        {title}
      </a>

      {/* 3. Snippet — chat excerpt that produced the citation. BE-emitted
          snippets are markdown (tables, headings, lists from web fusion)
          so we delegate to ``MessageBody`` — same renderer the chat
          surface uses (``react-markdown`` + ``remark-gfm`` +
          ``rehype-sanitize``). No ``citations`` prop → ``[N]`` tokens
          are left as plain text instead of being turned into citation
          pills (web snippet doesn't carry an inner citation graph). */}
      {payload.snippet ? (
        <MessageBody
          markdown={normaliseMangledMarkdownTables(payload.snippet)}
          className="text-[13px]"
        />
      ) : null}

      {/* 4. URL preview (mono) — ANTI-PHISHING affordance shown BEFORE the
          CTA so the destination is visible during decision time, not
          after the click (Gmail / Outlook / Cursor convention; reordered
          Jun 2026 per UI/UX review W2). */}
      <p
        className="truncate font-mono text-[11px] text-ink-3"
        aria-label={i18n._(msg`URL: ${payload.url}`)}
        title={payload.url}
      >
        {payload.url}
      </p>

      {/* 5. Published date — meta row below the URL, near the CTA. Only
          rendered when the BE provides a valid ISO timestamp. */}
      {formattedDate ? (
        <p className="text-[11px] text-ink-3">{formattedDate}</p>
      ) : null}

      {/* 6. Primary CTA — last commitment surface. Visible button affordance
          duplicates the title link target for screen-magnifier users.
          Copy reads "Apri su {host}" (Linear / Notion convention) instead
          of generic "Apri pagina" — the host re-anchors the destination
          one last time before the click commits. */}
      <a
        href={payload.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "mt-auto inline-flex w-fit items-center gap-1.5 self-start rounded-full",
          "border border-ando-orange/30 bg-ando-orange/10 px-3.5 py-1.5",
          "font-medium text-[12.5px] text-ando-orange",
          "transition-all hover:border-ando-orange/40 hover:bg-ando-orange/15",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        )}
      >
        <span>{i18n._(msg`Apri su ${host}`)}</span>
        <ArrowUpRight size={13} strokeWidth={1.75} aria-hidden />
      </a>
    </article>
  );
};
WebPreview.displayName = "WebPreview";
